/**
 * Inherent Risks API
 * Handles API calls for fetching inherent risk data
 *
 * @endpoint POST /api/sodms/risks
 * @param {GetInherentRisksRequest} payload - Request parameters
 * @param {boolean} payload.includeDetails - Include detailed risk information
 * @param {boolean} payload.includeViolationSummary - Include summary information
 * @param {number[]} payload.riskKeys - Array of risk IDs to fetch
 * @param {string} payload.columnToSort - Column name for sorting
 * @param {string} payload.globalSearch - Search text to find
 * @param {number} payload.max - Maximum number of records to return
 * @param {number} payload.offset - Offset for pagination
 * @param {string} payload.order - Sort order (ASC/DESC)
 *
 * @returns {Object} Response object
 * @returns {RiskItem[]} returns.data - Array of risk items with properties like riskId, riskName, priority, etc.
 * @returns {number} returns.totalRecords - Total number of records available
 */
import { createApi } from '@reduxjs/toolkit/query/react';

import { axiosBaseQuery } from '../../../../../../shared/services/baseQuery';
import { detailsActions } from '../../../../data-store/detailsSlice';
import {
  setRisksData,
  setRisksIsLoading,
  setRisksTotalRecords,
} from '../data-store';

import {
  type GetInherentRisksApiResponse,
  type GetInherentRisksRequest,
  InherentRisksType,
  type RiskItem,
} from './inherentRisksTypes';

export const inherentRisksApi = createApi({
  reducerPath: InherentRisksType.API_SLICE_NAME,
  baseQuery: axiosBaseQuery({}),
  keepUnusedDataFor: 60, // Cache for 1 minute
  endpoints: (builder) => ({
    getInherentRisks: builder.mutation<
      { data: RiskItem[]; totalRecords: number },
      GetInherentRisksRequest
    >({
      query: (payload: GetInherentRisksRequest) => ({
        url: '/api/sodms/risks',
        method: 'POST',
        data: payload,
        isECMv6: true,
      }),
      transformResponse: (response: GetInherentRisksApiResponse) => {
        if (response.status === 200) {
          return {
            data: response.data,
            totalRecords: response.totalRecords,
          };
        }

        return { data: [], totalRecords: 0 };
      },
      onQueryStarted: (payload, { dispatch }) => {
        const isSidePanelRequest =
          payload.max === 1 && payload.riskKeys.length === 1;

        if (!isSidePanelRequest) {
          dispatch(setRisksIsLoading(true));
          dispatch(detailsActions.setEntitlementRisksLoading(true));
        }
      },
      onCacheEntryAdded: async (
        payload,
        { dispatch, cacheDataLoaded, cacheEntryRemoved }
      ) => {
        const isSidePanelRequest =
          payload.max === 1 && payload.riskKeys.length === 1;

        try {
          const result = await cacheDataLoaded;

          // Only update main list state if this is not a single item request (sidepanel)
          if (!isSidePanelRequest) {
            dispatch(setRisksData(result.data.data));
            dispatch(setRisksTotalRecords(result.data.totalRecords));
            dispatch(setRisksIsLoading(false));
            dispatch(detailsActions.setEntitlementRisksData(result.data.data));
            dispatch(
              detailsActions.setEntitlementRisksTotalRecords(
                result.data.totalRecords
              )
            );
          }
        } catch (error) {
          if (!isSidePanelRequest) {
            dispatch(setRisksIsLoading(false));
            dispatch(
              detailsActions.setEntitlementRisksError(
                error instanceof Error ? error.message : 'Unknown error'
              )
            );
          }
        }

        await cacheEntryRemoved;
      },
    }),
  }),
});

export const { useGetInherentRisksMutation } = inherentRisksApi;
